/**
 * React wrapper for Bryntum Scheduler
 */
import React from 'react';
import { AlignSpec, AssignmentModel, AssignmentStoreConfig, Base, CellCopyPaste, CellCopyPasteConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnLines, ColumnLinesConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, ContainerItemConfig, ContainerLayoutConfig, CrudManager, CrudManagerConfig, DependencyModel, DependencyModelConfig, DependencyStoreConfig, EventCopyPaste, EventCopyPasteConfig, EventDrag, EventDragConfig, EventDragCreate, EventDragCreateConfig, EventDragSelect, EventDragSelectConfig, EventEdit, EventEditConfig, EventFilter, EventFilterConfig, EventMenu, EventMenuConfig, EventModel, EventModelConfig, EventNonWorkingTime, EventNonWorkingTimeConfig, EventStoreConfig, EventTooltip, EventTooltipConfig, ExcelExporter, ExcelExporterConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, GridCellEdit, GridCellEditConfig, GridColumnConfig, GridTreeGroup, GridTreeGroupConfig, Group, GroupConfig, GroupSummary, GroupSummaryConfig, HeaderMenu, HeaderMenuConfig, HeaderZoom, HeaderZoomConfig, Layout, Location, Mask, MaskConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, NonWorkingTime, NonWorkingTimeConfig, Pan, PanConfig, PanelCollapser, PanelHeader, PresetStore, ProjectModel, ProjectModelConfig, QuickFind, QuickFindConfig, Rectangle, RegionResize, RegionResizeConfig, ResourceHeaderConfig, ResourceMenu, ResourceMenuConfig, ResourceModel, ResourceModelConfig, ResourceStoreConfig, ResourceTimeRangeModel, ResourceTimeRangeModelConfig, ResourceTimeRangeStore, ResourceTimeRangeStoreConfig, ResourceTimeRanges, ResourceTimeRangesConfig, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowReorder, RowReorderConfig, ScheduleContext, ScheduleContextConfig, ScheduleMenu, ScheduleMenuConfig, ScheduleTooltip, ScheduleTooltipConfig, SchedulerAssignmentModel, SchedulerAssignmentModelConfig, SchedulerAssignmentStore, SchedulerDependencies, SchedulerDependenciesConfig, SchedulerDependencyEdit, SchedulerDependencyEditConfig, SchedulerDependencyModel, SchedulerDependencyStore, SchedulerEventModel, SchedulerEventResize, SchedulerEventResizeConfig, SchedulerEventStore, SchedulerFeaturesConfigType, SchedulerLabels, SchedulerLabelsConfig, SchedulerMultiPageExporter, SchedulerMultiPageExporterConfig, SchedulerMultiPageVerticalExporter, SchedulerMultiPageVerticalExporterConfig, SchedulerPdfExport, SchedulerPdfExportConfig, SchedulerProjectModel, SchedulerResourceModel, SchedulerResourceStore, SchedulerSinglePageExporter, SchedulerSinglePageExporterConfig, SchedulerSummary, SchedulerSummaryConfig, ScrollManager, ScrollManagerConfig, Scroller, ScrollerConfig, Search, SearchConfig, SimpleEventEdit, SimpleEventEditConfig, Sort, SortConfig, StateProvider, StickyCells, StickyCellsConfig, StickyEvents, StickyEventsConfig, Store, StoreConfig, Stripe, StripeConfig, SubGridConfig, TabConfig, TimeAxis, TimeAxisColumnConfig, TimeAxisConfig, TimeAxisHeaderMenu, TimeAxisHeaderMenuConfig, TimeRanges, TimeRangesConfig, TimeSelection, TimeSelectionConfig, TimeSpan, TimeSpanConfig, TimelineBase, Tool, ToolConfig, ToolbarConfig, TooltipConfig, Tree, TreeConfig, VerticalTimeAxisColumnConfig, ViewPreset, ViewPresetConfig, Widget } from '@bryntum/gantt';
import { Scheduler } from '@bryntum/gantt';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumSchedulerProps =  {
     // Configs only
    adopt?                            : HTMLElement|string
    align?                            : AlignSpec|string
    allowCreate?                      : boolean
    allowOver?                        : boolean
    allowOverlap?                     : boolean
    anchor?                           : boolean
    anchorToTarget?                   : boolean
    animateRemovingRows?              : boolean
    ariaDescription?                  : string
    ariaLabel?                        : string
    autoAdjustTimeAxis?               : boolean
    autoClose?                        : boolean
    autoHeight?                       : boolean
    autoShow?                         : boolean
    autoUpdateRecord?                 : boolean
    bbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                          : string|object
    bubbleEvents?                     : object
    bufferCoef?                       : number
    bufferThreshold?                  : number
    callOnFunctions?                  : boolean
    centered?                         : boolean
    closable?                         : boolean
    closeAction?                      : 'hide'|'destroy'
    collapsed?                        : boolean
    collapsible?                      : boolean|PanelCollapser
    columns?                          : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                           : object
    constrainTo?                      : HTMLElement|Widget|Rectangle
    contentElementCls?                : string|object
    contextMenuTriggerEvent?          : 'contextmenu'|'click'|'dblclick'
    createEventOnDblClick?            : boolean|object
    creationTooltip?                  : Partial<TooltipConfig>
    crudManagerClass?                 : typeof CrudManager
    date?                             : Date
    dateFormat?                       : string
    defaultBindProperty?              : string
    defaultFocus?                     : Function
    defaultRegion?                    : string
    defaultResourceImageName?         : string
    defaults?                         : object
    descriptionRenderer?              : Function
    deselectOnClick?                  : boolean
    destroyStore?                     : boolean
    destroyStores?                    : boolean
    disableGridRowModelWarning?       : boolean
    dismissDelay?                     : number
    dock?                             : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                        : boolean|object
    durationDisplayPrecision?         : number|boolean
    enableDeleteKey?                  : boolean
    enableRecurringEvents?            : boolean
    enableSticky?                     : boolean
    enableTextSelection?              : boolean
    endParamName?                     : string
    eventBarTextField?                : string
    eventBodyTemplate?                : Function
    eventRenderer?                    : Function
    eventRendererThisObj?             : object
    eventSelectionDisabled?           : boolean
    eventStyle?                       : 'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null
    features?                         : object
    fillLastColumn?                   : boolean
    fixedRowHeight?                   : boolean
    floating?                         : boolean
    focusOnToFront?                   : boolean
    footer?                           : object|string
    forElement?                       : HTMLElement
    forSelector?                      : string
    fullRowRefresh?                   : boolean
    getDateConstraints?               : Function
    getHtml?                          : Function|string
    getRowHeight?                     : Function
    header?                           : string|boolean|PanelHeader
    hideAnimation?                    : boolean|object
    hideDelay?                        : number
    hideHeaders?                      : boolean
    hideOnDelegateChange?             : boolean
    hideWhenEmpty?                    : boolean
    highlightPredecessors?            : boolean
    highlightSuccessors?              : boolean
    horizontalEventSorterFn?          : Function
    hoverDelay?                       : number
    htmlCls?                          : string|object
    ignoreParentReadOnly?             : boolean
    infiniteScroll?                   : boolean
    itemCls?                          : string
    keyMap?                           : Record<string, string>
    lazyItems?                        : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                        : object
    loadingMsg?                       : string
    loadMask?                         : string|Partial<MaskConfig>|null
    loadMaskDefaults?                 : Partial<MaskConfig>|Mask
    loadMaskError?                    : Partial<MaskConfig>|Mask|boolean
    localeClass?                      : typeof Base
    localizable?                      : boolean
    localizableProperties?            : string[]
    maintainSelectionOnDatasetChange? : boolean
    managedEventSizing?               : boolean
    maskDefaults?                     : Partial<MaskConfig>
    masked?                           : boolean|string|Partial<MaskConfig>
    maximizable?                      : boolean
    maximized?                        : boolean
    maxTimeAxisUnit?                  : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    modal?                            : boolean
    mode?                             : 'horizontal'|'vertical'
    monitorResize?                    : boolean
    mouseOffsetX?                     : number
    mouseOffsetY?                     : number
    multiEventSelect?                 : boolean
    namedItems?                       : Record<string, Partial<ContainerItemConfig>>
    narrowEventWidth?                 : number
    owner?                            : Widget
    partner?                          : TimelineBase
    passStartEndParameters?           : boolean
    plugins?                          : Function[]
    positioned?                       : boolean
    preCalculateHeightLimit?          : number
    preserveFocusOnDatasetChange?     : boolean
    preserveScrollOnDatasetChange?    : boolean
    preventTooltipOnTouch?            : boolean
    range?                            : 'day'|'week'|'month'
    relayStoreEvents?                 : boolean
    resizeToFitIncludesHeader?        : boolean
    resourceColumns?                  : Partial<ResourceHeaderConfig>
    resourceImageExtension?           : string
    resourceImagePath?                : string
    responsiveLevels?                 : Record<string, number|string>
    ripple?                           : boolean|object
    rootElement?                      : ShadowRoot
    scrollAction?                     : 'hide'|'realign'|null
    scrollerClass?                    : typeof Scroller
    scrollManager?                    : Partial<ScrollManagerConfig>|ScrollManager
    selectionMode?                    : object
    showAnimation?                    : boolean|object
    showCreationTooltip?              : boolean
    showDirty?                        : boolean
    showOnClick?                      : boolean
    showOnHover?                      : boolean
    showRecurringUI?                  : boolean
    showTooltip?                      : boolean
    showTooltipWhenDisabled?          : boolean
    snapRelativeToEventStartDate?     : boolean
    startParamName?                   : string
    stateful?                         : boolean|object|string[]
    statefulEvents?                   : object|string[]
    stateId?                          : string
    stateProvider?                    : StateProvider
    stepUnit?                         : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    stickyHeaders?                    : boolean
    strips?                           : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                   : Record<string, Partial<SubGridConfig>>
    suppressFit?                      : boolean
    syncMask?                         : string|Partial<MaskConfig>|null
    tab?                              : boolean|Partial<TabConfig>
    tag?                              : string
    tbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    terminalCls?                      : string
    terminalSides?                    : string[]
    textAlign?                        : 'left'|'center'|'right'|'start'|'end'
    textContent?                      : boolean
    timeAxis?                         : Partial<TimeAxisConfig>|TimeAxis
    timeAxisColumn?                   : Partial<TimeAxisColumnConfig>
    trackMouse?                       : boolean
    trapFocus?                        : boolean
    triggerSelectionChangeOnRemove?   : boolean
    ui?                               : string|object
    verticalTimeAxisColumn?           : Partial<VerticalTimeAxisColumnConfig>
    visibleZoomFactor?                : number
    weekStartDay?                     : number
    weight?                           : number
    zoomKeepsOriginalTimespan?        : boolean
    zoomOnMouseWheel?                 : boolean
    zoomOnTimeAxisDoubleClick?        : boolean

     // Configs and properties
    alignSelf?                     : string
    allowDropOnEventBar?           : boolean
    appendTo?                      : HTMLElement|string
    assignments?                   : AssignmentModel[]|object[]|SchedulerAssignmentModel[]|Partial<SchedulerAssignmentModelConfig>[]
    assignmentStore?               : SchedulerAssignmentStore|Partial<AssignmentStoreConfig>
    barMargin?                     : number
    cls?                           : string|object
    columnLines?                   : boolean
    content?                       : string
    creationTooltipTemplate?       : Function
    crudManager?                   : CrudManager|Partial<CrudManagerConfig>
    data?                          : object[]|Model[]|Partial<ModelConfig>[]
    dataset?                       : object
    dateSeparator?                 : string
    dependencies?                  : DependencyModel[]|Partial<DependencyModelConfig>[]|SchedulerDependencyModel[]
    dependencyStore?               : SchedulerDependencyStore|Partial<DependencyStoreConfig>
    descriptionFormat?             : string|string[]|boolean[]
    disabled?                      : boolean
    displayDateFormat?             : string
    emptyText?                     : string
    enableEventAnimations?         : boolean
    enableUndoRedoKeys?            : boolean
    endDate?                       : Date|string
    eventColor?                    : 'red'|'pink'|'purple'|'violet'|'indigo'|'blue'|'cyan'|'teal'|'green'|'lime'|'yellow'|'orange'|'deep-orange'|'gray'|'gantt-green'|null
    eventLayout?                   : 'stack'|'pack'|'mixed'|'none'|object
    events?                        : EventModel[]|Partial<EventModelConfig>[]|SchedulerEventModel[]
    eventStore?                    : SchedulerEventStore|Partial<EventStoreConfig>
    extraData?                     : string|number|boolean|object
    fillTicks?                     : boolean
    flex?                          : number|string
    forceFit?                      : boolean
    height?                        : number|string
    hidden?                        : boolean
    html?                          : string|Function
    id?                            : string
    insertBefore?                  : HTMLElement|string
    insertFirst?                   : HTMLElement|string
    isEventSelectable?             : Function
    items?                         : Widget[]|Partial<ContainerItemConfig>[]|Record<string, Partial<ContainerItemConfig>>|Record<string, Partial<ContainerItemConfig>|boolean|null>
    layout?                        : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?                   : object
    longPressTime?                 : number
    margin?                        : number|string
    maxHeight?                     : string|number
    maxWidth?                      : string|number
    maxZoomLevel?                  : number
    milestoneAlign?                : 'start'|'center'|'end'
    milestoneCharWidth?            : number
    milestoneLayoutMode?           : 'default'|'estimate'|'data'|'measure'
    milestoneTextPosition?         : 'inside'|'outside'|'always-outside'
    minHeight?                     : string|number
    minWidth?                      : string|number
    minZoomLevel?                  : number
    overlappingEventSorter?        : Function
    presets?                       : PresetStore|Partial<ViewPresetConfig>[]
    project?                       : ProjectModel|SchedulerProjectModel|Partial<ProjectModelConfig>
    readOnly?                      : boolean
    record?                        : Model
    resourceMargin?                : number
    resources?                     : ResourceModel[]|Partial<ResourceModelConfig>[]|SchedulerResourceModel[]
    resourceStore?                 : SchedulerResourceStore|Partial<ResourceStoreConfig>
    resourceTimeRanges?            : ResourceTimeRangeModel[]|Partial<ResourceTimeRangeModelConfig>[]
    resourceTimeRangeStore?        : ResourceTimeRangeStore|Partial<ResourceTimeRangeStoreConfig>
    rowHeight?                     : number
    scrollable?                    : Scroller|boolean|Partial<ScrollerConfig>
    snap?                          : boolean
    startDate?                     : Date|string
    store?                         : Store|Partial<StoreConfig>
    strictRecordMapping?           : boolean
    tickSize?                      : number
    timeRanges?                    : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeRangeStore?                : Store|Partial<StoreConfig>
    timeZone?                      : string|number
    title?                         : string
    tools?                         : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?                       : string|Partial<TooltipConfig>
    tooltipTemplate?               : Function
    transitionDuration?            : number
    updateTimelineContextOnScroll? : boolean
    useInitialAnimation?           : boolean|string
    viewPreset?                    : ViewPreset|string|Partial<ViewPresetConfig>
    visibleDate?                   : object|Date
    width?                         : number|string
    workingTime?                   : object
    x?                             : number
    y?                             : number

     // Properties only
    anchorSize?          : number[]
    eventColors?         : string[]
    eventStyles?         : string[]
    isEngineReady?       : boolean
    isSettingValues?     : boolean
    isValid?             : boolean
    scrollLeft?          : number
    scrollTop?           : number
    scrollX?             : number
    selectedAssignments? : SchedulerAssignmentModel[]
    selectedCell?        : Location
    selectedCells?       : Location[]
    selectedEvents?      : SchedulerEventModel[]
    selectedRecord?      : Model
    selectedRecords?     : Model[]|number[]
    selectedRows?        : Model[]|number[]
    state?               : object
    timeResolution?      : object|number
    type?                : string
    values?              : Record<string, object>
    zoomLevel?           : number

     // Events
    onAfterDependencySave?             : Function
    onAfterDragCreate?                 : Function
    onAfterEventDrop?                  : Function
    onAfterEventEdit?                  : Function
    onAfterEventSave?                  : Function
    onAssignmentSelectionChange?       : Function
    onBeforeAssignmentDelete?          : Function
    onBeforeAssignmentSelectionChange? : Function
    onBeforeCancelCellEdit?            : Function
    onBeforeCellDelete?                : Function
    onBeforeCellEditStart?             : Function
    onBeforeClose?                     : Function
    onBeforeColumnDragStart?           : Function
    onBeforeColumnDropFinalize?        : Function
    onBeforeCopy?                      : Function
    onBeforeDependencyAdd?             : Function
    onBeforeDependencyDelete?          : Function
    onBeforeDependencyEdit?            : Function
    onBeforeDependencyEditShow?        : Function
    onBeforeDependencySave?            : Function
    onBeforeDestroy?                   : Function
    onBeforeDragCreate?                : Function
    onBeforeDragCreateFinalize?        : Function
    onBeforeEventAdd?                  : Function
    onBeforeEventDelete?               : Function
    onBeforeEventDrag?                 : Function
    onBeforeEventDragSelect?           : Function
    onBeforeEventDropFinalize?         : Function
    onBeforeEventEdit?                 : Function
    onBeforeEventEditShow?             : Function
    onBeforeEventResize?               : Function
    onBeforeEventResizeFinalize?       : Function
    onBeforeEventSave?                 : Function
    onBeforeEventSelectionChange?      : Function
    onBeforeFinishCellEdit?            : Function
    onBeforeHide?                      : Function
    onBeforePan?                       : Function
    onBeforePaste?                     : Function
    onBeforePdfExport?                 : Function
    onBeforePresetChange?              : Function
    onBeforeRenderRow?                 : Function
    onBeforeRenderRows?                : Function
    onBeforeSelectionChange?           : Function
    onBeforeSetRecord?                 : Function
    onBeforeShow?                      : Function
    onBeforeToggleGroup?               : Function
    onBeforeToggleNode?                : Function
    onCancelCellEdit?                  : Function
    onCatchAll?                        : Function
    onCellClick?                       : Function
    onCellContextMenu?                 : Function
    onCellDblClick?                    : Function
    onCellMenuBeforeShow?              : Function
    onCellMenuItem?                    : Function
    onCellMenuShow?                    : Function
    onCellMenuToggleItem?              : Function
    onCellMouseOut?                    : Function
    onCellMouseOver?                   : Function
    onCollapseNode?                    : Function
    onColumnDragStart?                 : Function
    onColumnDrop?                      : Function
    onContextMenuItem?                 : Function
    onContextMenuToggleItem?           : Function
    onCopy?                            : Function
    onDataChange?                      : Function
    onDateRangeChange?                 : Function
    onDependenciesDrawn?               : Function
    onDependencyClick?                 : Function
    onDependencyDblClick?              : Function
    onDependencyMouseOut?              : Function
    onDependencyMouseOver?             : Function
    onDestroy?                         : Function
    onDragCreateEnd?                   : Function
    onDragCreateStart?                 : Function
    onDragSelecting?                   : Function
    onEventAutoCreated?                : Function
    onEventClick?                      : Function
    onEventContextMenu?                : Function
    onEventDblClick?                   : Function
    onEventDrag?                       : Function
    onEventDragAbort?                  : Function
    onEventDragModeChange?             : Function
    onEventDragReset?                  : Function
    onEventDragStart?                  : Function
    onEventDrop?                       : Function
    onEventEditBeforeSetRecord?        : Function
    onEventKeyDown?                    : Function
    onEventKeyUp?                      : Function
    onEventMenuBeforeShow?             : Function
    onEventMenuItem?                   : Function
    onEventMenuShow?                   : Function
    onEventMouseDown?                  : Function
    onEventMouseEnter?                 : Function
    onEventMouseLeave?                 : Function
    onEventMouseOut?                   : Function
    onEventMouseOver?                  : Function
    onEventMouseUp?                    : Function
    onEventPartialResize?              : Function
    onEventResizeEnd?                  : Function
    onEventResizeStart?                : Function
    onEventSelectionChange?            : Function
    onExpandNode?                      : Function
    onFinishCellEdit?                  : Function
    onFocusIn?                         : Function
    onFocusOut?                        : Function
    onGridRowAbort?                    : Function
    onGridRowBeforeDragStart?          : Function
    onGridRowBeforeDropFinalize?       : Function
    onGridRowDrag?                     : Function
    onGridRowDragStart?                : Function
    onGridRowDrop?                     : Function
    onHeaderMenuBeforeShow?            : Function
    onHeaderMenuItem?                  : Function
    onHeaderMenuShow?                  : Function
    onHeaderMenuToggleItem?            : Function
    onHide?                            : Function
    onMouseOut?                        : Function
    onMouseOver?                       : Function
    onNavigate?                        : Function
    onPaint?                           : Function
    onPaste?                           : Function
    onPasteNotAllowed?                 : Function
    onPdfExport?                       : Function
    onPointerOver?                     : Function
    onPresetChange?                    : Function
    onReadOnly?                        : Function
    onRecompose?                       : Function
    onReleaseEvent?                    : Function
    onRenderEvent?                     : Function
    onRenderRow?                       : Function
    onRenderRows?                      : Function
    onResize?                          : Function
    onResourceHeaderClick?             : Function
    onResourceHeaderContextmenu?       : Function
    onResourceHeaderDblclick?          : Function
    onResourceMenuBeforeShow?          : Function
    onResourceMenuItem?                : Function
    onResourceMenuShow?                : Function
    onResourceMenuToggleItem?          : Function
    onResourceTimeRangeClick?          : Function
    onResourceTimeRangeContextMenu?    : Function
    onResourceTimeRangeDblClick?       : Function
    onResourceTimeRangeMouseDown?      : Function
    onResourceTimeRangeMouseOut?       : Function
    onResourceTimeRangeMouseOver?      : Function
    onResourceTimeRangeMouseUp?        : Function
    onResponsive?                      : Function
    onScheduleClick?                   : Function
    onScheduleContextMenu?             : Function
    onScheduleDblClick?                : Function
    onScheduleMenuBeforeShow?          : Function
    onScheduleMenuItem?                : Function
    onScheduleMenuShow?                : Function
    onScheduleMouseDown?               : Function
    onScheduleMouseEnter?              : Function
    onScheduleMouseLeave?              : Function
    onScheduleMouseMove?               : Function
    onScheduleMouseUp?                 : Function
    onScroll?                          : Function
    onSelectionChange?                 : Function
    onSelectionModeChange?             : Function
    onShow?                            : Function
    onStartCellEdit?                   : Function
    onSubGridCollapse?                 : Function
    onSubGridExpand?                   : Function
    onTimeAxisChange?                  : Function
    onTimeAxisHeaderClick?             : Function
    onTimeAxisHeaderContextMenu?       : Function
    onTimeAxisHeaderDblClick?          : Function
    onTimeAxisHeaderMenuBeforeShow?    : Function
    onTimeAxisHeaderMenuItem?          : Function
    onTimeAxisHeaderMenuShow?          : Function
    onTimelineContextChange?           : Function
    onTimelineViewportResize?          : Function
    onTimeRangeHeaderClick?            : Function
    onTimeRangeHeaderContextMenu?      : Function
    onTimeRangeHeaderDblClick?         : Function
    onTimeSelectionChange?             : Function
    onTimeSelectionElementClick?       : Function
    onToggleGroup?                     : Function
    onToggleNode?                      : Function
    onToolClick?                       : Function
    onVisibleDateRangeChange?          : Function

     // Features
    cellCopyPasteFeature?       : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?            : object|boolean|string|GridCellEdit|Partial<GridCellEditConfig>
    cellMenuFeature?            : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?         : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?     : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature?   : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnLinesFeature?         : object|boolean|string|ColumnLines|Partial<ColumnLinesConfig>
    columnPickerFeature?        : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?        : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?       : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?        : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    dependenciesFeature?        : object|boolean|string|SchedulerDependencies|Partial<SchedulerDependenciesConfig>
    dependencyEditFeature?      : object|boolean|string|SchedulerDependencyEdit|Partial<SchedulerDependencyEditConfig>
    eventCopyPasteFeature?      : object|boolean|string|EventCopyPaste|Partial<EventCopyPasteConfig>
    eventDragFeature?           : object|boolean|string|EventDrag|Partial<EventDragConfig>
    eventDragCreateFeature?     : object|boolean|string|EventDragCreate|Partial<EventDragCreateConfig>
    eventDragSelectFeature?     : object|boolean|string|EventDragSelect|Partial<EventDragSelectConfig>
    eventEditFeature?           : object|boolean|string|EventEdit|Partial<EventEditConfig>
    eventFilterFeature?         : object|boolean|string|EventFilter|Partial<EventFilterConfig>
    eventMenuFeature?           : object|boolean|string|EventMenu|Partial<EventMenuConfig>
    eventNonWorkingTimeFeature? : object|boolean|string|EventNonWorkingTime|Partial<EventNonWorkingTimeConfig>
    eventResizeFeature?         : object|boolean|string|SchedulerEventResize|Partial<SchedulerEventResizeConfig>
    eventTooltipFeature?        : object|boolean|string|EventTooltip|Partial<EventTooltipConfig>
    excelExporterFeature?       : object|boolean|string|ExcelExporter|Partial<ExcelExporterConfig>
    fillHandleFeature?          : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?              : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?           : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?               : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?        : object|boolean|string|GroupSummary|Partial<GroupSummaryConfig>
    headerMenuFeature?          : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    headerZoomFeature?          : object|boolean|string|HeaderZoom|Partial<HeaderZoomConfig>
    labelsFeature?              : object|boolean|string|SchedulerLabels|Partial<SchedulerLabelsConfig>
    mergeCellsFeature?          : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    multipageFeature?           : object|boolean|string|SchedulerMultiPageExporter|Partial<SchedulerMultiPageExporterConfig>
    multipageverticalFeature?   : object|boolean|string|SchedulerMultiPageVerticalExporter|Partial<SchedulerMultiPageVerticalExporterConfig>
    nonWorkingTimeFeature?      : object|boolean|string|NonWorkingTime|Partial<NonWorkingTimeConfig>
    panFeature?                 : object|boolean|string|Pan|Partial<PanConfig>
    pdfExportFeature?           : object|boolean|string|SchedulerPdfExport|Partial<SchedulerPdfExportConfig>
    quickFindFeature?           : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?        : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    resourceMenuFeature?        : object|boolean|string|ResourceMenu|Partial<ResourceMenuConfig>
    resourceTimeRangesFeature?  : object|boolean|string|ResourceTimeRanges|Partial<ResourceTimeRangesConfig>
    rowCopyPasteFeature?        : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?         : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?          : object|boolean|string|RowReorder|Partial<RowReorderConfig>
    scheduleContextFeature?     : object|boolean|string|ScheduleContext|Partial<ScheduleContextConfig>
    scheduleMenuFeature?        : object|boolean|string|ScheduleMenu|Partial<ScheduleMenuConfig>
    scheduleTooltipFeature?     : object|boolean|string|ScheduleTooltip|Partial<ScheduleTooltipConfig>
    searchFeature?              : object|boolean|string|Search|Partial<SearchConfig>
    simpleEventEditFeature?     : object|boolean|string|SimpleEventEdit|Partial<SimpleEventEditConfig>
    singlepageFeature?          : object|boolean|string|SchedulerSinglePageExporter|Partial<SchedulerSinglePageExporterConfig>
    sortFeature?                : object|boolean|string|Sort|Partial<SortConfig>
    stickyCellsFeature?         : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stickyEventsFeature?        : object|boolean|string|StickyEvents|Partial<StickyEventsConfig>
    stripeFeature?              : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?             : object|boolean|string|SchedulerSummary|Partial<SchedulerSummaryConfig>
    timeAxisHeaderMenuFeature?  : object|boolean|string|TimeAxisHeaderMenu|Partial<TimeAxisHeaderMenuConfig>
    timeRangesFeature?          : object|boolean|string|TimeRanges|Partial<TimeRangesConfig>
    timeSelectionFeature?       : object|boolean|string|TimeSelection|Partial<TimeSelectionConfig>
    treeFeature?                : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?           : object|boolean|string|GridTreeGroup|Partial<GridTreeGroupConfig>

}

export class BryntumScheduler extends React.Component<BryntumSchedulerProps> {

    static instanceClass = Scheduler;

    static instanceName = 'Scheduler';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnLinesFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'dependenciesFeature',
        'dependencyEditFeature',
        'eventCopyPasteFeature',
        'eventDragFeature',
        'eventDragCreateFeature',
        'eventDragSelectFeature',
        'eventEditFeature',
        'eventFilterFeature',
        'eventMenuFeature',
        'eventNonWorkingTimeFeature',
        'eventResizeFeature',
        'eventTooltipFeature',
        'excelExporterFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'headerZoomFeature',
        'labelsFeature',
        'mergeCellsFeature',
        'multipageFeature',
        'multipageverticalFeature',
        'nonWorkingTimeFeature',
        'panFeature',
        'pdfExportFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'resourceMenuFeature',
        'resourceTimeRangesFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'scheduleContextFeature',
        'scheduleMenuFeature',
        'scheduleTooltipFeature',
        'searchFeature',
        'simpleEventEditFeature',
        'singlepageFeature',
        'sortFeature',
        'stickyCellsFeature',
        'stickyEventsFeature',
        'stripeFeature',
        'summaryFeature',
        'timeAxisHeaderMenuFeature',
        'timeRangesFeature',
        'timeSelectionFeature',
        'treeFeature',
        'treeGroupFeature'
    ];

    projectStores = false;

    dataStores = {
        'assignmentStore': 'assignments',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'resourceTimeRangeStore': 'resourceTimeRanges',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adopt',
        'align',
        'allowCreate',
        'allowOver',
        'allowOverlap',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoAdjustTimeAxis',
        'autoClose',
        'autoHeight',
        'autoShow',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'bufferCoef',
        'bufferThreshold',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'createEventOnDblClick',
        'creationTooltip',
        'crudManagerClass',
        'date',
        'dateFormat',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaultResourceImageName',
        'defaults',
        'descriptionRenderer',
        'deselectOnClick',
        'destroyStore',
        'destroyStores',
        'disableGridRowModelWarning',
        'dismissDelay',
        'dock',
        'draggable',
        'durationDisplayPrecision',
        'enableDeleteKey',
        'enableRecurringEvents',
        'enableSticky',
        'enableTextSelection',
        'endParamName',
        'eventBarTextField',
        'eventBodyTemplate',
        'eventRenderer',
        'eventRendererThisObj',
        'eventSelectionDisabled',
        'eventStyle',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getDateConstraints',
        'getHtml',
        'getRowHeight',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHeaders',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'highlightPredecessors',
        'highlightSuccessors',
        'horizontalEventSorterFn',
        'hoverDelay',
        'htmlCls',
        'ignoreParentReadOnly',
        'infiniteScroll',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maintainSelectionOnDatasetChange',
        'managedEventSizing',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'maxTimeAxisUnit',
        'modal',
        'mode',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'multiEventSelect',
        'namedItems',
        'narrowEventWidth',
        'owner',
        'partner',
        'passStartEndParameters',
        'plugins',
        'positioned',
        'preCalculateHeightLimit',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'range',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'resourceColumns',
        'resourceImageExtension',
        'resourceImagePath',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'selectionMode',
        'showAnimation',
        'showCreationTooltip',
        'showDirty',
        'showOnClick',
        'showOnHover',
        'showRecurringUI',
        'showTooltip',
        'showTooltipWhenDisabled',
        'snapRelativeToEventStartDate',
        'startParamName',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'stepUnit',
        'stickyHeaders',
        'strips',
        'subGridConfigs',
        'suppressFit',
        'syncMask',
        'tab',
        'tag',
        'tbar',
        'terminalCls',
        'terminalSides',
        'textAlign',
        'textContent',
        'timeAxis',
        'timeAxisColumn',
        'trackMouse',
        'trapFocus',
        'triggerSelectionChangeOnRemove',
        'ui',
        'verticalTimeAxisColumn',
        'visibleZoomFactor',
        'weekStartDay',
        'weight',
        'zoomKeepsOriginalTimespan',
        'zoomOnMouseWheel',
        'zoomOnTimeAxisDoubleClick'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'allowDropOnEventBar',
        'appendTo',
        'assignments',
        'assignmentStore',
        'barMargin',
        'cls',
        'columnLines',
        'content',
        'creationTooltipTemplate',
        'crudManager',
        'data',
        'dataset',
        'dateSeparator',
        'dependencies',
        'dependencyStore',
        'descriptionFormat',
        'disabled',
        'displayDateFormat',
        'emptyText',
        'enableEventAnimations',
        'enableUndoRedoKeys',
        'endDate',
        'eventColor',
        'eventLayout',
        'events',
        'eventStore',
        'extraData',
        'fillTicks',
        'flex',
        'forceFit',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'isEventSelectable',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maxWidth',
        'maxZoomLevel',
        'milestoneAlign',
        'milestoneCharWidth',
        'milestoneLayoutMode',
        'milestoneTextPosition',
        'minHeight',
        'minWidth',
        'minZoomLevel',
        'onAfterDependencySave',
        'onAfterDragCreate',
        'onAfterEventDrop',
        'onAfterEventEdit',
        'onAfterEventSave',
        'onAssignmentSelectionChange',
        'onBeforeAssignmentDelete',
        'onBeforeAssignmentSelectionChange',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDependencyAdd',
        'onBeforeDependencyDelete',
        'onBeforeDependencyEdit',
        'onBeforeDependencyEditShow',
        'onBeforeDependencySave',
        'onBeforeDestroy',
        'onBeforeDragCreate',
        'onBeforeDragCreateFinalize',
        'onBeforeEventAdd',
        'onBeforeEventDelete',
        'onBeforeEventDrag',
        'onBeforeEventDragSelect',
        'onBeforeEventDropFinalize',
        'onBeforeEventEdit',
        'onBeforeEventEditShow',
        'onBeforeEventResize',
        'onBeforeEventResizeFinalize',
        'onBeforeEventSave',
        'onBeforeEventSelectionChange',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforePan',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforePresetChange',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapseNode',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDateRangeChange',
        'onDependenciesDrawn',
        'onDependencyClick',
        'onDependencyDblClick',
        'onDependencyMouseOut',
        'onDependencyMouseOver',
        'onDestroy',
        'onDragCreateEnd',
        'onDragCreateStart',
        'onDragSelecting',
        'onEventAutoCreated',
        'onEventClick',
        'onEventContextMenu',
        'onEventDblClick',
        'onEventDrag',
        'onEventDragAbort',
        'onEventDragModeChange',
        'onEventDragReset',
        'onEventDragStart',
        'onEventDrop',
        'onEventEditBeforeSetRecord',
        'onEventKeyDown',
        'onEventKeyUp',
        'onEventMenuBeforeShow',
        'onEventMenuItem',
        'onEventMenuShow',
        'onEventMouseDown',
        'onEventMouseEnter',
        'onEventMouseLeave',
        'onEventMouseOut',
        'onEventMouseOver',
        'onEventMouseUp',
        'onEventPartialResize',
        'onEventResizeEnd',
        'onEventResizeStart',
        'onEventSelectionChange',
        'onExpandNode',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onMouseOut',
        'onMouseOver',
        'onNavigate',
        'onPaint',
        'onPaste',
        'onPasteNotAllowed',
        'onPdfExport',
        'onPointerOver',
        'onPresetChange',
        'onReadOnly',
        'onRecompose',
        'onReleaseEvent',
        'onRenderEvent',
        'onRenderRow',
        'onRenderRows',
        'onResize',
        'onResourceHeaderClick',
        'onResourceHeaderContextmenu',
        'onResourceHeaderDblclick',
        'onResourceMenuBeforeShow',
        'onResourceMenuItem',
        'onResourceMenuShow',
        'onResourceMenuToggleItem',
        'onResourceTimeRangeClick',
        'onResourceTimeRangeContextMenu',
        'onResourceTimeRangeDblClick',
        'onResourceTimeRangeMouseDown',
        'onResourceTimeRangeMouseOut',
        'onResourceTimeRangeMouseOver',
        'onResourceTimeRangeMouseUp',
        'onResponsive',
        'onScheduleClick',
        'onScheduleContextMenu',
        'onScheduleDblClick',
        'onScheduleMenuBeforeShow',
        'onScheduleMenuItem',
        'onScheduleMenuShow',
        'onScheduleMouseDown',
        'onScheduleMouseEnter',
        'onScheduleMouseLeave',
        'onScheduleMouseMove',
        'onScheduleMouseUp',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onTimeAxisChange',
        'onTimeAxisHeaderClick',
        'onTimeAxisHeaderContextMenu',
        'onTimeAxisHeaderDblClick',
        'onTimeAxisHeaderMenuBeforeShow',
        'onTimeAxisHeaderMenuItem',
        'onTimeAxisHeaderMenuShow',
        'onTimelineContextChange',
        'onTimelineViewportResize',
        'onTimeRangeHeaderClick',
        'onTimeRangeHeaderContextMenu',
        'onTimeRangeHeaderDblClick',
        'onTimeSelectionChange',
        'onTimeSelectionElementClick',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onVisibleDateRangeChange',
        'overlappingEventSorter',
        'presets',
        'project',
        'readOnly',
        'record',
        'resourceMargin',
        'resources',
        'resourceStore',
        'resourceTimeRanges',
        'resourceTimeRangeStore',
        'rowHeight',
        'scrollable',
        'snap',
        'startDate',
        'store',
        'strictRecordMapping',
        'tickSize',
        'timeRanges',
        'timeRangeStore',
        'timeZone',
        'title',
        'tools',
        'tooltip',
        'tooltipTemplate',
        'transitionDuration',
        'updateTimelineContextOnScroll',
        'useInitialAnimation',
        'viewPreset',
        'visibleDate',
        'width',
        'workingTime',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'eventColors',
        'eventStyles',
        'isEngineReady',
        'isSettingValues',
        'isValid',
        'scrollLeft',
        'scrollTop',
        'scrollX',
        'selectedAssignments',
        'selectedCell',
        'selectedCells',
        'selectedEvents',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'timeResolution',
        'type',
        'values',
        'zoomLevel'
    ];

    // Component instance
    instance!: Scheduler;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumSchedulerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-${BryntumScheduler.instanceClass.$name.toLowerCase()}-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
